/**
 * The price an UNTYPED deal / quote / invoice line inherits (owner 2026-10-27).
 *
 * Project price entry first (`resolveProjectPrice`, lib/project-pricing.ts),
 * else the legacy catalogue rule (`proRataUnitPrice`, lib/city-pro-rata.ts).
 * Never re-implements either. Typed prices are never overwritten: that is the
 * caller's rule (only call this for an untouched line).
 *
 * Mirrors the backend's `src/lib/line-pricing.ts` `resolveLineUnitPrice`.
 */

import { proRataUnitPrice } from './city-pro-rata';
import { pricedTierIds, resolveProjectPrice, type ProjectPriceEntry } from './project-pricing';
import type { PriceTier } from './types';

export interface ComputeLinePriceInput {
  /** The line's project's entries (`[]` when none or not loaded). */
  entries: readonly ProjectPriceEntry[];
  defaultTierId: number | null;
  productId: number | null;
  /** Document currency (ISO code); `null` = unknown, so the catalogue rule. */
  currency: string | null;
  /** The line's tier; `null` = default tier. */
  priceTierId: number | null;
  selectedCityCount: number;
  projectCityCount: number;
  allProjectCities?: boolean;
  quantity: number;
  catalogUnitPrice: string | null;
}

export interface ComputedLinePrice {
  unitPrice: string | null;
  /** Send only when non-null (a quantity override that does not divide into whole cents). */
  discountAmount: string | null;
  /** Project price: the tier actually used. Catalogue: the line's tier, unchanged. */
  priceTierId: number | null;
  source: 'project_price' | 'catalogue';
}

export function computeLinePrice(input: ComputeLinePriceInput): ComputedLinePrice {
  if (
    input.defaultTierId !== null &&
    input.productId !== null &&
    input.currency !== null &&
    input.currency.trim() !== '' &&
    input.entries.length > 0
  ) {
    let resolved = null;
    try {
      resolved = resolveProjectPrice({
        entries: input.entries,
        productId: input.productId,
        currency: input.currency,
        tierId: input.priceTierId,
        defaultTierId: input.defaultTierId,
        selectedCityCount: input.selectedCityCount,
        projectCityCount: input.projectCityCount,
        allProjectCities: input.allProjectCities === true,
        quantity: input.quantity,
      });
    } catch {
      // A count the resolver cannot read (e.g. a fractional quantity): legacy rule.
      resolved = null;
    }
    if (resolved !== null) {
      return {
        unitPrice: resolved.unitPrice,
        discountAmount: resolved.discountAmount,
        priceTierId: resolved.tierId,
        source: 'project_price',
      };
    }
  }
  return {
    unitPrice: proRataUnitPrice({
      catalogUnitPrice: input.catalogUnitPrice,
      projectCityCount: input.projectCityCount,
      selectedCityCount: input.selectedCityCount,
      allProjectCities: input.allProjectCities === true,
    }),
    discountAmount: null,
    priceTierId: input.priceTierId,
    source: 'catalogue',
  };
}

/**
 * Tiers the line picker offers: those with a price for this product and
 * currency, plus the default tier always. In the master's sort order.
 */
export function tierOptionsFor(
  entries: readonly ProjectPriceEntry[],
  tiers: readonly PriceTier[],
  productId: number | null,
  currency: string | null,
): PriceTier[] {
  const priced =
    productId === null || currency === null ? new Set<number>() : new Set(pricedTierIds(entries, productId, currency));
  return tiers
    .filter((t) => t.isDefault || priced.has(t.id))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}
